import type { Asset, Document, Node, Page } from "@canvas/schema";

/**
 * Document operations (SPEC.md §2 "Edit model"). Every op is addressed by id (never by array
 * path) and `applyOp` returns the inverse op, so undo is just applying inverses in reverse.
 * Ops are plain JSON, so they could later be sent over the wire or mapped onto a CRDT.
 */
export type Op =
  | { op: "insert"; nodes: Node[]; parentId: string; index: number }
  | { op: "remove"; id: string }
  | { op: "update"; id: string; set: Partial<Node> }
  | { op: "move"; id: string; parentId: string; index: number }
  | { op: "page.insert"; page: Page; nodes: Node[]; index: number }
  | { op: "page.remove"; id: string }
  | { op: "page.update"; id: string; set: Partial<Omit<Page, "id" | "children">> }
  | { op: "page.move"; id: string; index: number }
  | { op: "doc.update"; set: { name?: string } }
  | { op: "asset.add"; asset: Asset }
  | { op: "asset.remove"; id: string };

export class OpError extends Error {}

type Container = { children: string[] };

function getContainer(doc: Document, id: string): Container | null {
  const page = doc.pages.find((p) => p.id === id);
  if (page) return page;
  const n = doc.nodes[id];
  return n && (n.type === "group" || n.type === "frame") ? n : null;
}

/** Collect a node and all its descendants (parents before children). */
export function subtree(doc: Document, id: string): Node[] {
  const out: Node[] = [];
  const walk = (nid: string) => {
    const n = doc.nodes[nid];
    if (!n) return;
    out.push(n);
    if (n.type === "group" || n.type === "frame") n.children.forEach(walk);
  };
  walk(id);
  return out;
}

/** True if `ancestor` is `id` or contains it. */
export function isAncestor(doc: Document, ancestor: string, id: string): boolean {
  let cur: string | undefined = id;
  while (cur) {
    if (cur === ancestor) return true;
    cur = doc.nodes[cur]?.parentId;
  }
  return false;
}

/**
 * Immutable helper: copies only what changes. Pages array and the touched container are
 * copied; the nodes map is copied once per op.
 */
function withContainer(doc: Document, id: string, f: (children: string[]) => string[]): Document {
  const pi = doc.pages.findIndex((p) => p.id === id);
  if (pi >= 0) {
    const pages = doc.pages.slice();
    pages[pi] = { ...pages[pi], children: f(pages[pi].children) };
    return { ...doc, pages };
  }
  const n = doc.nodes[id];
  if (!n || (n.type !== "group" && n.type !== "frame")) throw new OpError(`container ${id} not found`);
  return { ...doc, nodes: { ...doc.nodes, [id]: { ...n, children: f(n.children) } } };
}

const clampIndex = (i: number, len: number) => Math.max(0, Math.min(len, i));

export function applyOp(doc: Document, op: Op): { doc: Document; inverse: Op } {
  switch (op.op) {
    case "insert": {
      const root = op.nodes[0];
      if (!root) throw new OpError("insert without nodes");
      if (doc.nodes[root.id]) throw new OpError(`node ${root.id} already exists`);
      if (!getContainer(doc, op.parentId)) throw new OpError(`container ${op.parentId} not found`);
      const nodes = { ...doc.nodes };
      for (const n of op.nodes) nodes[n.id] = n;
      nodes[root.id] = { ...root, parentId: op.parentId };
      let next: Document = { ...doc, nodes };
      next = withContainer(next, op.parentId, (c) => {
        const out = c.slice();
        out.splice(clampIndex(op.index, c.length), 0, root.id);
        return out;
      });
      return { doc: next, inverse: { op: "remove", id: root.id } };
    }
    case "remove": {
      const n = doc.nodes[op.id];
      if (!n) throw new OpError(`node ${op.id} not found`);
      const parent = getContainer(doc, n.parentId);
      const index = parent ? parent.children.indexOf(op.id) : -1;
      const removed = subtree(doc, op.id);
      const nodes = { ...doc.nodes };
      for (const r of removed) delete nodes[r.id];
      let next: Document = { ...doc, nodes };
      if (parent) next = withContainer(next, n.parentId, (c) => c.filter((x) => x !== op.id));
      return { doc: next, inverse: { op: "insert", nodes: removed, parentId: n.parentId, index } };
    }
    case "update": {
      const n = doc.nodes[op.id];
      if (!n) throw new OpError(`node ${op.id} not found`);
      const prev: Record<string, unknown> = {};
      for (const k of Object.keys(op.set)) {
        if (k === "id" || k === "type" || k === "parentId" || k === "children") throw new OpError(`cannot update ${k}; use move/insert`);
        prev[k] = (n as Record<string, unknown>)[k];
      }
      return {
        doc: { ...doc, nodes: { ...doc.nodes, [op.id]: { ...n, ...op.set } as Node } },
        inverse: { op: "update", id: op.id, set: prev as Partial<Node> },
      };
    }
    case "move": {
      const n = doc.nodes[op.id];
      if (!n) throw new OpError(`node ${op.id} not found`);
      if (!getContainer(doc, op.parentId)) throw new OpError(`container ${op.parentId} not found`);
      if (isAncestor(doc, op.id, op.parentId)) throw new OpError("cannot move a node into itself");
      const oldParent = getContainer(doc, n.parentId)!;
      const oldIndex = oldParent.children.indexOf(op.id);
      let next = withContainer(doc, n.parentId, (c) => c.filter((x) => x !== op.id));
      next = withContainer(next, op.parentId, (c) => {
        const out = c.slice();
        out.splice(clampIndex(op.index, c.length), 0, op.id);
        return out;
      });
      next = { ...next, nodes: { ...next.nodes, [op.id]: { ...next.nodes[op.id], parentId: op.parentId } } };
      return { doc: next, inverse: { op: "move", id: op.id, parentId: n.parentId, index: oldIndex } };
    }
    case "page.insert": {
      if (doc.pages.some((p) => p.id === op.page.id)) throw new OpError(`page ${op.page.id} exists`);
      const pages = doc.pages.slice();
      pages.splice(clampIndex(op.index, pages.length), 0, op.page);
      const nodes = { ...doc.nodes };
      for (const n of op.nodes) nodes[n.id] = n;
      return { doc: { ...doc, pages, nodes }, inverse: { op: "page.remove", id: op.page.id } };
    }
    case "page.remove": {
      const index = doc.pages.findIndex((p) => p.id === op.id);
      if (index < 0) throw new OpError(`page ${op.id} not found`);
      if (doc.pages.length === 1) throw new OpError("cannot remove the last page");
      const page = doc.pages[index];
      const removed = page.children.flatMap((id) => subtree(doc, id));
      const nodes = { ...doc.nodes };
      for (const r of removed) delete nodes[r.id];
      return {
        doc: { ...doc, pages: doc.pages.filter((p) => p.id !== op.id), nodes },
        inverse: { op: "page.insert", page, nodes: removed, index },
      };
    }
    case "page.update": {
      const index = doc.pages.findIndex((p) => p.id === op.id);
      if (index < 0) throw new OpError(`page ${op.id} not found`);
      const page = doc.pages[index];
      const prev: Record<string, unknown> = {};
      for (const k of Object.keys(op.set)) prev[k] = (page as Record<string, unknown>)[k];
      const pages = doc.pages.slice();
      pages[index] = { ...page, ...op.set };
      return { doc: { ...doc, pages }, inverse: { op: "page.update", id: op.id, set: prev } };
    }
    case "page.move": {
      const from = doc.pages.findIndex((p) => p.id === op.id);
      if (from < 0) throw new OpError(`page ${op.id} not found`);
      const pages = doc.pages.slice();
      const [p] = pages.splice(from, 1);
      pages.splice(clampIndex(op.index, pages.length), 0, p);
      return { doc: { ...doc, pages }, inverse: { op: "page.move", id: op.id, index: from } };
    }
    case "doc.update": {
      const prev: { name?: string } = {};
      if ("name" in op.set) prev.name = doc.name;
      return { doc: { ...doc, ...op.set }, inverse: { op: "doc.update", set: prev } };
    }
    case "asset.add": {
      if (doc.assets[op.asset.id]) return { doc, inverse: { op: "doc.update", set: {} } };
      return { doc: { ...doc, assets: { ...doc.assets, [op.asset.id]: op.asset } }, inverse: { op: "asset.remove", id: op.asset.id } };
    }
    case "asset.remove": {
      const asset = doc.assets[op.id];
      if (!asset) throw new OpError(`asset ${op.id} not found`);
      const assets = { ...doc.assets };
      delete assets[op.id];
      return { doc: { ...doc, assets }, inverse: { op: "asset.add", asset } };
    }
  }
}

/** Apply ops in order; returns the inverses in undo order (reversed). */
export function applyOps(doc: Document, ops: Op[]): { doc: Document; inverse: Op[] } {
  const inverse: Op[] = [];
  let cur = doc;
  for (const op of ops) {
    const r = applyOp(cur, op);
    cur = r.doc;
    inverse.push(r.inverse);
  }
  return { doc: cur, inverse: inverse.reverse() };
}
